const responseBody = (response) => {
  const payload = response?.data ?? response;
  return payload?.data ?? payload;
};

const orderValue = (assessment) => {
  if (assessment?.assessment_date) {
    const timestamp = Date.parse(assessment.assessment_date);
    if (!Number.isNaN(timestamp)) return timestamp;
  }
  return Number.isFinite(assessment?.assessment_order)
    ? assessment.assessment_order
    : null;
};

export const normalizeGazeReportResponse = (
  response,
  { seniorId, reportId },
) => {
  const body = responseBody(response);
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new Error("The gaze report response is invalid.");
  }
  if (body.seniorId != null && String(body.seniorId) !== String(seniorId)) {
    throw new Error("The gaze report does not belong to the requested person.");
  }
  if (body.reportId != null && String(body.reportId) !== String(reportId)) {
    throw new Error("The gaze report identifier does not match the request.");
  }

  const assessments = Array.isArray(body.assessments)
    ? body.assessments
    : body.assessment && typeof body.assessment === "object"
      ? [body.assessment]
      : [];
  const selected = assessments.find(
    (assessment) =>
      String(assessment?.parent_report_id || "") === String(reportId),
  );
  if (!selected) {
    throw new Error(
      "The requested gaze assessment is missing from the response.",
    );
  }
  if (!selected.subject_id) {
    throw new Error("The requested gaze assessment has no subject identifier.");
  }

  const duplicateSelected = assessments.filter(
    (assessment) =>
      String(assessment?.parent_report_id || "") === String(reportId),
  );
  if (duplicateSelected.length !== 1) {
    throw new Error("The response contains duplicate gaze report identifiers.");
  }

  const selectedOrder = orderValue(selected);
  const seenReportIds = new Set();
  const history = assessments.filter((assessment) => {
    if (String(assessment?.subject_id || "") !== String(selected.subject_id)) {
      return false;
    }
    const id = String(assessment?.parent_report_id || "");
    if (!id || seenReportIds.has(id)) return false;
    seenReportIds.add(id);
    if (assessment === selected) return true;
    const value = orderValue(assessment);
    return selectedOrder != null && value != null && value <= selectedOrder;
  });

  history.sort((left, right) => {
    if (left === selected) return 1;
    if (right === selected) return -1;
    return (orderValue(left) ?? 0) - (orderValue(right) ?? 0);
  });

  return {
    schemaVersion: body.schemaVersion || "gaze-report-v1",
    seniorId,
    reportId,
    assessments: history,
  };
};

// Validates the trends endpoint (compact per-session summaries). The report
// treats trends as optional, so callers fall back to no trend when this throws.
export const normalizeGazeTrendsResponse = (
  response,
  { seniorId, reportId },
) => {
  const body = responseBody(response);
  if (!body || typeof body !== "object" || !Array.isArray(body.sessions)) {
    throw new Error("The gaze trends response is invalid.");
  }
  if (body.seniorId != null && String(body.seniorId) !== String(seniorId)) {
    throw new Error("The gaze trends do not belong to the requested person.");
  }
  if (
    body.throughReportId != null &&
    String(body.throughReportId) !== String(reportId)
  ) {
    throw new Error("The gaze trends do not end at the requested report.");
  }

  const seenReportIds = new Set();
  const sessions = body.sessions.map((session) => {
    const id = String(session?.parent_report_id || "");
    if (!id || seenReportIds.has(id)) {
      throw new Error("The gaze trends contain a missing or duplicate report.");
    }
    seenReportIds.add(id);
    if (orderValue(session) == null) {
      throw new Error("The gaze trends contain an undated session.");
    }
    return session;
  });

  for (let i = 1; i < sessions.length; i += 1) {
    if (orderValue(sessions[i]) < orderValue(sessions[i - 1])) {
      throw new Error("The gaze trends are not ordered oldest to newest.");
    }
  }
  const last = sessions[sessions.length - 1];
  if (!last || String(last.parent_report_id) !== String(reportId)) {
    throw new Error("The gaze trends do not end at the requested report.");
  }
  return sessions;
};
